import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './styles/main.css';
import { ThemeProvider } from './ThemeContext';

// Eagerly loaded — needed on first paint
import Login    from './pages/Login';
import Register from './pages/Register';
import Landing  from './pages/Landing';

// Lazy loaded — only fetched when the route is visited
const Home          = lazy(() => import('./pages/Home'));
const Products      = lazy(() => import('./pages/Products'));
const MyProducts    = lazy(() => import('./pages/MyProducts'));
const Team          = lazy(() => import('./pages/Team'));
const Mine          = lazy(() => import('./pages/Mine'));
const Recharge      = lazy(() => import('./pages/Recharge'));
const Withdraw      = lazy(() => import('./pages/Withdraw'));
const History       = lazy(() => import('./pages/History'));
const Help          = lazy(() => import('./pages/Help'));
const BankAccounts  = lazy(() => import('./pages/BankAccounts'));
const AboutUs       = lazy(() => import('./pages/AboutUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const QueeKLoan     = lazy(() => import('./pages/QueeKLoan'));
const LoanReferrals = lazy(() => import('./pages/LoanReferrals'));
const VestTrades    = lazy(() => import('./pages/VestTrades'));
const News          = lazy(() => import('./pages/News'));
const VestAffiliate = lazy(() => import('./pages/VestAffiliate'));
const AffiliateTerms = lazy(() => import('./pages/AffiliateTerms'));
const Transfer = lazy(() => import('./pages/Transfer'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--gray-100)',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        border: '3px solid var(--blue-pale)',
        borderTopColor: 'var(--blue-mid)',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function isLoggedIn() {
  return !!localStorage.getItem('vg_token');
}

// Decides what to render at "/" based on auth status and referral param
function RootRoute() {
  const location = useLocation();
  const ref = new URLSearchParams(location.search).get('ref');
  if (isLoggedIn()) return <Home />;
  if (ref) return <Navigate to={`/register?ref=${ref}`} replace />;
  return <Landing />;
}

function PrivateRoute({ children }) {
  const location = useLocation();
  if (!isLoggedIn()) {
    const ref = new URLSearchParams(location.search).get('ref');
    // Carry the referral code to the register page instead of dropping it on login
    return ref
      ? <Navigate to={`/register?ref=${ref}`} replace />
      : <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" toastOptions={{ style: { borderRadius: '10px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px' } }} />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/" element={<RootRoute />} />
        <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
        <Route path="/my-products" element={<PrivateRoute><MyProducts /></PrivateRoute>} />
        <Route path="/team" element={<PrivateRoute><Team /></PrivateRoute>} />
        <Route path="/mine" element={<PrivateRoute><Mine /></PrivateRoute>} />
        <Route path="/recharge" element={<PrivateRoute><Recharge /></PrivateRoute>} />
        <Route path="/withdraw" element={<PrivateRoute><Withdraw /></PrivateRoute>} />
        <Route path="/history" element={<PrivateRoute><History /></PrivateRoute>} />
        <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
        <Route path="/bank-accounts" element={<PrivateRoute><BankAccounts /></PrivateRoute>} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/queekloan"           element={<PrivateRoute><QueeKLoan /></PrivateRoute>} />
        <Route path="/queekloan/referrals" element={<PrivateRoute><LoanReferrals /></PrivateRoute>} />
        <Route path="/vesttrades" element={<PrivateRoute><VestTrades /></PrivateRoute>} />
        <Route path="/news"            element={<PrivateRoute><News /></PrivateRoute>} />
        <Route path="/affiliate"       element={<PrivateRoute><VestAffiliate /></PrivateRoute>} />
        <Route path="/affiliate-terms" element={<PrivateRoute><AffiliateTerms /></PrivateRoute>} />
        <Route path="/transfer"        element={<PrivateRoute><Transfer /></PrivateRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);